// import { App } from '@/components/models/App'
import { mapActions, mapState } from 'vuex'
import PushNotificationsController from '@/controllers/PushNotificationsController'
import { UserStates } from '@/components/conversations/enums/UserStates'
import AppsUseCases from '@/modules/Apps/application/AppsUseCases'
import AppsController from '@/modules/Apps/infrastructure/controllers/Apps.controller'
import EmitToast from '@/utils/EmitToast'
import link from '@/utils/links'
import { App } from '@/modules/Apps/domain/Apps'
import { KeybeIconType } from '@/shared/infrastructure/enums/icons/KeybeIconType'

// Components
import KeybeIcon from '@/shared/infrastructure/ui/icons/KeybeIcon.vue'
import KBMenu from '@/shared/infrastructure/ui/menus/KBMenu/KBMenu.vue'
import AdviserStatusController from '@/modules/conversations/infrastructure/controllers/AdviserStatus.controller'
import AuthService from '@/services/AuthService'
import BillingService from '@/components/configuration/Services/BillingService'

export default {
  props: {
    centerClass: {
      type: Boolean,
      default: false
    }
  },
  components: {
    KeybeIcon,
    KBMenu
  },
  data () {
    return {
      show: false,
      search: '',
      loadingCompanies: false,
      filteredApps: [],
      closeOnClick: false,
      awaitingSearch: false,
      limit: 50,
      appsController: new AppsController(),
      KeybeIconType
    }
  },
  methods: {
    ...mapActions('AppStore', ['getAdviserStatus', 'setApps']),
    ...mapActions('UserStore', ['logout']),
    ...mapActions('BillingStore', ['setAppConfig']),
    async _getCompanies () {
      try {
        this.loadingCompanies = true
        this.filteredApps = this.apps
        this.loadingCompanies = false
      } catch (e) {
        console.error(e)
        this.loadingCompanies = false
      }
    },
    goToHome () {
      window.location.href = `${process.env.VUE_APP_BASE_KEYBE_URL}/home`
    },
    async _logout () {
      await PushNotificationsController.setOneSignalStatus({ active: false })
      await AdviserStatusController.inactiveIfIsAdviser(this.isAgent)
      await AuthService.logout()
      this.logout()
      window.location.href = `${process.env.VUE_APP_BASE_KEYBE_URL}/signin`
    },
    async filterApps () {
      if (!this.awaitingSearch) {
        setTimeout(async () => {
          const search = this.search ? this.search.toLowerCase() : ''
          const data = {
            token: this.token,
            search: search,
            offset: 0,
            limit: this.limit
          }
          const response = await AppsUseCases.getApps(this.appsController, data)
          if (Array.isArray(response)) this.setApps(response)
          else {
            EmitToast.emitErrors(response)
            if (response.code === 401) this._logout()
          }
          this.awaitingSearch = false
          this.filteredApps = this.apps
        }, 500)
      }
      this.awaitingSearch = true
    },
    goTo (link: string) {
      if (Object.keys(this.$route.query).length !== 0) {
        this.$router.replace({ path: this.$route.path, query: null })
      }
      if (this.$route.path === link) return

      this.$router.push(link)
    },
    async selectApp (app: App) {
      this.show = false
      if (app.inactive) {
        return EmitToast.emitErrors({
          message: this.$t('inactiveApp'),
          status: false
        })
      }
      if (
        this.selectedApp &&
        this.selectedApp?.uuid &&
        app?.uuid !== this.selectedApp?.uuid
      ) {
        await PushNotificationsController.setOneSignalStatus({
          active: false,
          appUUID: this.selectedApp?.uuid,
          companyUUID: this.selectedCompany?.uuid
        })
      }
      const data = {
        name: app.name,
        uuid: app.uuid,
        token: this.token
      }
      await AppsUseCases.selectApp(this.appsController, data)
      const currentStatus = await this.getAdviserStatus(this.user?.userId)
      const value = currentStatus === UserStates.Online
      await PushNotificationsController.setOneSignalStatus({ active: value })
      await this.getAppConfig()
      this.callRedirect()
    },
    async getAppConfig () {
      const appConfig = await BillingService.getAppConfig()
      this.setAppConfig(appConfig)
    },
    callRedirect () {
      if (!this.activeApp) {
        this.goTo(link.payment)
      } else {
        this.goTo(link.conversations)
      }
    }
  },
  watch: {
    inactiveApp () {
      this.callRedirect()
    }
  },
  computed: {
    ...mapState('AppStore', ['selectedApp', 'apps']),
    ...mapState('CompanyStore', ['selectedCompany']),
    ...mapState('UserStore', ['user', 'token', 'isAgent']),
    category () {
      if (!this.selectedApp) return ''
      return this.selectedApp.categoriesOther ? this.selectedApp.categoriesOther : this.selectedApp.categories?.[0] || ''
    },
    categoryIcon () {
      const icons = {
        car: KeybeIconType.AUTO_DETAILS,
        motorcycle: KeybeIconType.MOTO_DETAILS,
        realestate: KeybeIconType.REALESTATE,
        retail: KeybeIconType.SHOPPING_CART,
        education: KeybeIconType.EDUCATION,
        essential: KeybeIconType.PUZZLE_PIECE,
        keybe: KeybeIconType.KB
      }
      return icons[this.category] || KeybeIconType.PUZZLE_PIECE
    },
    categoryColor () {
      const categories = [
        { category: 'car', color: '#D72638' },
        { category: 'motorcycle', color: '#F46036' },
        { category: 'realestate', color: '#33658A' },
        { category: 'retail', color: '#6DBE45' },
        { category: 'education', color: '#4CB5F5' },
        { category: 'essential', color: '#A9A9A9' },
        { category: 'keybe', color: '#00ffdc' }
      ]
      const found = categories.find(c => c.category === this.category)
      return found ? found.color : '#A9A9A9'
    },
    storageRoute (): string {
      return process.env.VUE_APP_STORAGE_URL
    },
    activeApp (): boolean {
      const status = this.selectedApp?.status
      return status === 'active' || status == null || status === true || status === false || status === 200
    },
    appName () {
      return this.selectedApp?.name || this.$t('selectApp')
    },
    sectionName () {
      const route = this.$route.path
      if (route.includes('conversations')) {
        return 'SmartChat'
      } else if (route.includes('sales')) {
        return this.$t('Funnels')
      } else if (route.includes('people') || route.includes('businesses')) {
        return this.$t('CDP')
      } else if (route.includes('outbound')) {
        return this.$t('outbound')
      }
      return ''
    }
  },
  mounted () {
    this.$nextTick(() => {
      this._getCompanies()
    })
  }
}
